import { Head, Link } from "@inertiajs/react"
import { ChevronLeft } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"
import { DataTable, DataRow } from "@/components/ui/data-table"

type Application = {
  id: number
  first_name: string
  last_name: string
  email: string
  qualified: boolean
  booked_at: string | null
  website: string | null
  podcast_situation: string
  paid_clients: string
  client_ltv: string
  willing_to_invest: boolean
  relationship_willingness: string
  referrer: string | null
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  created_at: string
}

const SITUATION: Record<string, string> = {
  launch: "Ready to launch a new show",
  scale: "Has a show, wants to scale it",
  guidance: "Has an idea, needs guidance",
  overwhelmed: "Started, feeling overwhelmed",
}
const PAID_CLIENTS: Record<string, string> = {
  none: "No paid clients yet",
  "1-5": "1–5",
  "5-20": "5–20",
  "20-50": "20–50",
  "50-plus": "50+",
}
const CLIENT_LTV: Record<string, string> = {
  "1-5k": "$1K–$5K",
  "5-10k": "$5K–$10K",
  "10-25k": "$10K–$25K",
  "25k-plus": "$25K+",
}
const RELATIONSHIP: Record<string, string> = {
  active: "Yes, will be actively involved",
  with_guidance: "Open to it with guidance",
  outsource: "Prefers to fully outsource it",
}

export default function AdminApplicationShow({ application }: { application: Application }) {
  const name = `${application.first_name} ${application.last_name}`
  const hasTracking =
    application.referrer || application.utm_source || application.utm_medium || application.utm_campaign

  return (
    <>
      <Head title={`Application — ${name}`}>
        <meta name="description" content="Fit Call application detail." />
        <meta property="og:title" content="Application detail" />
        <meta property="og:description" content="Fit Call application detail." />
      </Head>
      <AdminShell>
        <Link
          href="/admin/applications"
          className="inline-flex items-center gap-1 text-sm text-ink-muted no-underline hover:text-ink-display"
        >
          <ChevronLeft className="h-4 w-4" />
          All applications
        </Link>

        <div className="mt-4 flex flex-wrap items-center gap-3 border-b border-hairline pb-6">
          <h1>{name}</h1>
          {application.qualified ? (
            <Badge tone="accent">Qualified</Badge>
          ) : (
            <Badge tone="muted">Not yet</Badge>
          )}
          {application.booked_at && <Badge tone="signal">Fit Call booked</Badge>}
        </div>

        <DataTable className="mt-6">
          <DataRow title="Fit Call">
            {application.booked_at
              ? `Booked — ${formatDateTime(application.booked_at)}`
              : "Not booked yet"}
          </DataRow>
          <DataRow title="Email">
            <a href={`mailto:${application.email}`}>{application.email}</a>
          </DataRow>
          <DataRow title="Website / LinkedIn">
            {application.website ? (
              <a href={application.website} target="_blank" rel="noopener noreferrer">
                {application.website}
              </a>
            ) : (
              <span className="text-ink-muted">—</span>
            )}
          </DataRow>
          <DataRow title="Podcast situation">
            {SITUATION[application.podcast_situation] ?? application.podcast_situation}
          </DataRow>
          <DataRow title="Paid clients">
            {PAID_CLIENTS[application.paid_clients] ?? application.paid_clients}
          </DataRow>
          <DataRow title="Client lifetime value">
            {CLIENT_LTV[application.client_ltv] ?? application.client_ltv}
          </DataRow>
          <DataRow title="Willing to invest">
            {application.willing_to_invest ? "Yes" : "No"}
          </DataRow>
          <DataRow title="Relationship-building">
            {RELATIONSHIP[application.relationship_willingness] ?? application.relationship_willingness}
          </DataRow>
          <DataRow title="Submitted">{formatDateTime(application.created_at)}</DataRow>
          {hasTracking && (
            <DataRow title="Source">
              <div className="space-y-1 text-sm text-ink-muted">
                {application.referrer && <div>Referrer: {application.referrer}</div>}
                {application.utm_source && <div>utm_source: {application.utm_source}</div>}
                {application.utm_medium && <div>utm_medium: {application.utm_medium}</div>}
                {application.utm_campaign && <div>utm_campaign: {application.utm_campaign}</div>}
              </div>
            </DataRow>
          )}
        </DataTable>
      </AdminShell>
    </>
  )
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
